import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "frontend-components/lib/utils";

const cardVariants = cva("group/card rounded-card flex flex-col overflow-hidden", {
    variants: {
        variant: {
            plain: "bg-compendia-card text-text-primary gap-6 py-6",
            solution:
                "hover:shadow-card-hover min-h-[390px] cursor-pointer px-[30px] py-10 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-[3px]",
        },
        surface: {
            "teal-dark": "bg-surface-teal-dark text-white",
            "teal-light": "bg-surface-teal-light text-text-primary",
            bone: "bg-surface-bone text-text-primary",
            greige: "bg-surface-greige text-text-primary",
        },
        size: {
            default: "",
            sm: "",
        },
    },
    compoundVariants: [{ variant: "plain", size: "sm", class: "gap-4 py-4" }],
    defaultVariants: {
        variant: "plain",
        size: "default",
    },
});

function Card({
    className,
    variant = "plain",
    surface,
    size = "default",
    ...props
}: React.ComponentProps<"div"> & VariantProps<typeof cardVariants>) {
    return (
        <div
            data-slot="card"
            data-size={size ?? undefined}
            data-variant={variant ?? undefined}
            className={cn(cardVariants({ variant, surface, size, className }))}
            {...props}
        />
    );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="card-header"
            className={cn(
                "@container/card-header grid auto-rows-min items-start gap-1 px-[30px] group-data-[size=sm]/card:px-4 has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto] [.border-b]:pb-6 group-data-[size=sm]/card:[.border-b]:pb-4",
                className,
            )}
            {...props}
        />
    );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="card-title"
            className={cn(
                "text-base leading-normal font-medium group-data-[size=sm]/card:text-sm",
                className,
            )}
            {...props}
        />
    );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="card-description"
            className={cn("text-text-muted text-sm", className)}
            {...props}
        />
    );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="card-action"
            className={cn(
                "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
                className,
            )}
            {...props}
        />
    );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="card-content"
            className={cn(
                "px-[30px] group-data-[size=sm]/card:px-4 group-data-[variant=solution]/card:mt-auto group-data-[variant=solution]/card:p-0",
                className,
            )}
            {...props}
        />
    );
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="card-footer"
            className={cn(
                "flex items-center px-[30px] group-data-[size=sm]/card:px-4 [.border-t]:pt-6 group-data-[size=sm]/card:[.border-t]:pt-4",
                className,
            )}
            {...props}
        />
    );
}

function CardIcon({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="card-icon"
            className={cn(
                "mb-6 inline-flex [&_svg]:size-9",
                "group-data-[surface=teal-dark]/card:[&_svg]:stroke-white/65",
                "group-data-[surface=bone]/card:[&_svg]:stroke-compendia-black/40",
                "group-data-[surface=greige]/card:[&_svg]:stroke-compendia-black/40",
                "group-data-[surface=teal-light]/card:[&_svg]:stroke-compendia-black/40",
                className,
            )}
            {...props}
        />
    );
}

function CardCategory({ className, ...props }: React.ComponentProps<"p">) {
    return (
        <p
            data-slot="card-category"
            className={cn(
                "text-category tracking-category text-text-muted font-medium uppercase",
                "group-data-[surface=teal-dark]/card:text-white/50",
                "group-data-[surface=bone]/card:text-text-on-bone",
                "group-data-[surface=greige]/card:text-text-on-bone",
                "group-data-[surface=teal-light]/card:text-text-on-teal-light",
                className,
            )}
            {...props}
        />
    );
}

export {
    Card,
    CardHeader,
    CardFooter,
    CardTitle,
    CardAction,
    CardDescription,
    CardContent,
    CardIcon,
    CardCategory,
    cardVariants,
};
